/**
 * What the app tells the user about their AI quota - pure (no React, no network), so it can be unit tested
 * (`npm run test:ai-chat`).
 *
 * The numbers come from the server (GET /v1/ai/quota, the user's own Durable Object): the plan is derived there
 * from the licence, never chosen here. The user sees MESSAGES used / limit per month (Standard 100, Premium
 * 1000); a plan without AI (free, expired trial) has limit 0 and a clear "not included" state. A hidden cost budget
 * on the server can end the month early - the server then answers exactly as if the messages were used up, so
 * there is one "reached" state here.
 */

export type AiPlanName = "free" | "standard" | "premium"

export interface QuotaStatus {
  allowed: boolean
  used: number
  limit: number
  remaining: number
  plan?: AiPlanName
  reason?: "no_plan" | "quota"
  inputTokens?: number
  outputTokens?: number
  resetsAt: string
}

export type QuotaState = "ok" | "warning" | "exhausted" | "no_plan"

export interface QuotaView {
  state: QuotaState
  used: number
  limit: number
  remaining: number
  plan: AiPlanName | undefined
  resetsAt: string
}

/** Translate-function shape (`i18next.t` / react-i18next's `t`): English text is the key. */
export type TFn = (key: string, options?: Record<string, unknown>) => string

const WARNING_THRESHOLD = 0.8

export function quotaView(q: QuotaStatus): QuotaView {
  const base = { used: q.used, limit: q.limit, remaining: q.remaining, plan: q.plan, resetsAt: q.resetsAt }
  if (q.limit <= 0 || q.reason === "no_plan") return { ...base, state: "no_plan" }
  if (!q.allowed) return { ...base, state: "exhausted" }
  if (q.used / q.limit >= WARNING_THRESHOLD) return { ...base, state: "warning" }
  return { ...base, state: "ok" }
}

const defaultFormatDate = (iso: string): string => new Date(iso).toLocaleDateString()

/** The short text next to the chat title: "37/100", or "No AI" on a plan without it. */
export function quotaChipText(v: QuotaView, t: TFn): string {
  if (v.state === "no_plan") return t("No AI")
  return `${v.used}/${v.limit}`
}

/** The tooltip of that chip, with what to do about it. */
export function quotaChipTitle(v: QuotaView, t: TFn, formatDate: (iso: string) => string = defaultFormatDate): string {
  if (v.state === "no_plan") return t("AI is not included in your plan. Upgrade to Standard or Premium to use it.")
  const date = formatDate(v.resetsAt)
  const base = t("AI messages used this month: {{used}} of {{limit}}. Resets {{date}}.", { used: v.used, limit: v.limit, date })
  if (v.state === "exhausted" && v.plan === "standard") return `${base} ${t("Upgrade to Premium for more.")}`
  return base
}

/** The notice above the input when the user cannot send (null while they can). */
export function quotaNotice(v: QuotaView, t: TFn, formatDate: (iso: string) => string = defaultFormatDate): string | null {
  if (v.state === "no_plan") return t("AI is not included in your plan. Upgrade to Standard or Premium to use it.")
  if (v.state === "exhausted") return refusalText("quota", v.plan, v.resetsAt, t, formatDate)
  return null
}

function refusalText(
  reason: "no_plan" | "quota",
  plan: AiPlanName | undefined,
  resetsAt: string | undefined,
  t: TFn,
  formatDate: (iso: string) => string,
): string {
  if (reason === "no_plan") return t("AI is not included in your plan. Upgrade to Standard or Premium to use it.")
  const date = resetsAt ? formatDate(resetsAt) : t("next month")
  const reached = t("You have reached this month's AI limit. It resets {{date}}.", { date })
  return plan === "premium" ? reached : `${reached} ${t("Upgrade to Premium for more.")}`
}

/** An error whose message is already user-safe and translated; only these are shown to the user as they are. */
export class AiUserMessageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "AiUserMessageError"
  }
}

/**
 * The user-facing text for an error code in an AI stream (`ai_provider_error` or anything unknown): never the
 * server's own text, which can be a provider's raw error.
 */
export function aiStreamErrorMessage(t: TFn, code?: unknown): string {
  if (code === "ai_bad_request") return t("Something went wrong. Please try again.")
  return t("AI is temporarily unavailable. Try again in a moment.")
}

/**
 * The message for a refused AI request, from the HTTP status and JSON body of the answer (null: not a refusal
 * this knows - the caller shows its own error).
 */
export function aiRefusalMessage(
  status: number,
  body: unknown,
  t: TFn,
  formatDate: (iso: string) => string = defaultFormatDate,
): string | null {
  const b = (body ?? {}) as { error?: unknown; reason?: unknown; quota?: Partial<QuotaStatus> }
  if (status === 429 && b.error === "ai_quota_exceeded") {
    const reason = b.reason === "no_plan" || b.quota?.reason === "no_plan" ? "no_plan" : "quota"
    return refusalText(reason, b.quota?.plan, b.quota?.resetsAt, t, formatDate)
  }
  if (status === 429 && b.error === "ai_turn_limit") {
    return t("That took too many steps for one message. Try a smaller request.")
  }
  if (status === 503 && b.error === "ai_quota_unavailable") {
    return t("AI is temporarily unavailable. Try again in a moment.")
  }
  return null
}
